/* Settings — appearance, the week's first day, reminders in this browser,
   the account. Everything but the browser's own push subscription is synced. */
import { useCallback, useEffect, useState } from 'react';
import {
  connectionLabel,
  disconnectConnection,
  listConnections,
  type ServerConnection,
  type ThemePref,
  useApi,
  useAuth,
  useEntitlement,
  usePreferences,
  WEEKDAY_NAMES,
  type WeekStart,
} from '@habishine/client-core';

import { Dialog } from '../components/Dialog';
import { Icon } from '../components/Icon';
import { Avatar, Button, Card, CardTitle, Page, PageHeader, Segmented } from '../components/ui';
import { PaywallPanel } from './PaywallPage';
import { billingAvailable, managementUrl } from '../platform/billing';
import { deregisterFromPush, isPushRegistered, permissionState, pushSupported, registerForPush } from '../push';

const THEMES: { value: ThemePref; label: string }[] = [
  { value: 'system', label: 'Match system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const { themePref, setThemePref, weekStartsOn, setWeekStartsOn, remindersEnabled, setRemindersEnabled } = usePreferences();
  const [signingOut, setSigningOut] = useState(false);

  return (
    <Page className="screen-in">
      <PageHeader hue="frog" eyebrow="Preferences" title="Settings" sub="Synced with your other devices." />
      <div className="space-y-4">
        <Card className="rise-in" >
          <CardTitle>Appearance</CardTitle>
          <Segmented value={themePref} onChange={setThemePref} options={THEMES} />
        </Card>

        <Card className="rise-in" >
          <CardTitle>Week starts on</CardTitle>
          <Segmented
            className="flex-wrap"
            value={weekStartsOn}
            onChange={(v) => setWeekStartsOn(v as WeekStart)}
            options={WEEKDAY_NAMES.map((name, i) => ({ value: i as WeekStart, label: name.slice(0, 3), title: name }))}
          />
        </Card>

        <NotificationsCard remindersEnabled={remindersEnabled} setRemindersEnabled={setRemindersEnabled} />

        <SubscriptionCard />

        <ConnectedAppsCard />

        <Card className="rise-in" >
          <CardTitle>Account</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <Avatar email={user?.email} size={40} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold text-ink">{user?.email ?? 'Signed in'}</div>
              <div className="text-[12.5px] text-meta">Account deletion is managed in the mobile app for now.</div>
            </div>
            <Button busy={signingOut} onClick={() => { setSigningOut(true); void signOut().finally(() => setSigningOut(false)); }}>
              Sign out
            </Button>
          </div>
        </Card>

        <Card className="rise-in hidden md:block" >
          <CardTitle>Keyboard</CardTitle>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[13.5px] text-ink-2 sm:grid-cols-3">
            {([['⌘K', 'Search or jump anywhere'], ['N', 'Focus capture'], ['T', 'Today'], ['P', 'Plan'], ['R', 'Review'], ['H', 'Habits'], [',', 'Settings'], ['Esc', 'Close anything']] as const).map(([k, l]) => (
              <div key={k} className="flex items-center gap-2.5"><kbd className="kbd min-w-[26px] text-center">{k}</kbd>{l}</div>
            ))}
          </div>
        </Card>
      </div>
    </Page>
  );
}

/* The subscription — the web half of docs/subscriptions.md §10. One card says
   what the plan is and offers the ONE action that fits it: manage (a web
   subscription → RevenueCat's customer portal), choose a plan (none / lapsed →
   the paywall in a sheet), or nothing but the facts (an App Store subscription
   is managed on the phone; a comp has nothing to manage). The plan is read
   from the entitlement context, never asked of RevenueCat: the server is
   authoritative (§7), and a row that disagreed with the gate would be worse
   than no row. The route to cancellation has to be reachable from inside the
   product — the same rule Guideline 3.1.2 imposes on the phone, and the same
   reason: hunting for a cancel link is the dark pattern about.html says this
   app doesn't have. */
function SubscriptionCard() {
  const { plan, planReason, planStore } = useEntitlement();
  const [paywall, setPaywall] = useState(false);
  const [opening, setOpening] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const closePaywall = useCallback(() => setPaywall(false), []);

  const transferred = plan === 'expired' && planReason === 'transferred';
  const subtitle =
    plan === 'trialing'
      ? 'Free trial of Habishine Pro'
      : plan === 'pro'
        ? 'Habishine Pro'
        : plan === 'comped'
          ? 'Complimentary access'
          : transferred
            ? 'Moved to another account on your Apple ID'
            : plan === 'expired'
              ? 'Your subscription has ended'
              : 'No plan yet';

  /* Where it is billed decides where it is managed — for a plan that IS
     billed. A lapsed or absent plan has nothing to manage anywhere, so it gets
     no "billed through…" line (the row's last store is history, not an
     instruction). `planStore` is the server's word; while it is still unknown
     (an old cache, a server that predates the field) an entitled account is
     told both places rather than being sent to the wrong one. */
  const entitled = plan === 'trialing' || plan === 'pro';
  const where = !entitled
    ? null
    : planStore === 'web'
      ? 'Billed here, through your card.'
      : planStore === 'app_store'
        ? 'Billed through the App Store — manage or cancel it on your iPhone, in Settings → your name → Subscriptions.'
        : 'Manage it wherever you subscribed: here, or on your iPhone in Settings → your name → Subscriptions.';

  const openPortal = useCallback(async () => {
    if (opening) return;
    setOpening(true);
    setNote(null);
    const url = await managementUrl();
    setOpening(false);
    if (url) window.open(url, '_blank', 'noopener');
    // No portal means no WEB subscription on this customer — so it was bought
    // on the phone. Said plainly, rather than pointing at a receipt email that
    // Apple, not RevenueCat, sent.
    else setNote('This subscription isn’t billed here, so there’s nothing to manage on the web. If you subscribed on your iPhone, manage it in Settings → your name → Subscriptions.');
  }, [opening]);

  const canChoose = billingAvailable && (plan === 'none' || plan === 'expired');

  return (
    <Card className="rise-in">
      <CardTitle>Subscription</CardTitle>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-ink">{subtitle}</div>
          {where ? <div className="mt-0.5 max-w-[60ch] text-[12.5px] leading-[1.5] text-meta">{where}</div> : null}
          {note ? <div className="mt-1 text-[12.5px] text-replan">{note}</div> : null}
        </div>
        {entitled && (planStore === 'web' || (planStore === null && billingAvailable)) ? (
          <Button busy={opening} onClick={() => void openPortal()}>Manage subscription</Button>
        ) : null}
        {canChoose ? (
          <Button variant="primary" onClick={() => setPaywall(true)}>Choose a plan</Button>
        ) : null}
      </div>
      {paywall ? (
        <Dialog title="Choose a plan" onClose={closePaywall}>
          <PaywallPanel compact onPurchased={closePaywall} />
        </Dialog>
      ) : null}
    </Card>
  );
}

/* Reminders in THIS browser. Two things, kept apart the way the mobile Settings
   keeps them: `remindersEnabled` is the account's wish (synced, read by the
   dispatcher), and the push subscription is this browser's ability to receive.
   Turning on does both; turning off here only detaches this browser — the
   phone keeps its reminders unless the account switch is flipped. */
function NotificationsCard({
  remindersEnabled,
  setRemindersEnabled,
}: {
  remindersEnabled: boolean;
  setRemindersEnabled: (on: boolean) => void;
}) {
  const api = useApi();
  const [registered, setRegistered] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void isPushRegistered().then((r) => alive && setRegistered(r));
    return () => {
      alive = false;
    };
  }, []);

  const turnOn = async () => {
    setBusy(true);
    setNote(null);
    const res = await registerForPush(api);
    if (res.ok) {
      setRegistered(true);
      if (!remindersEnabled) setRemindersEnabled(true);
    } else {
      setNote(res.message);
    }
    setBusy(false);
  };
  const turnOff = async () => {
    setBusy(true);
    await deregisterFromPush(api);
    setRegistered(false);
    setBusy(false);
  };

  const permission = permissionState();
  return (
    <Card className="rise-in">
      <CardTitle right={registered ? <span className="flex items-center gap-1.5 rounded-full bg-[var(--hue-green-t)] px-2.5 py-1 text-[12px] font-bold text-[var(--hue-green-k)]"><Icon name="bell" size={12} strokeWidth={2.5} /> On here</span> : null}>Reminders in this browser</CardTitle>
      <p className="mb-4 max-w-[60ch] text-[14px] leading-[1.5] text-meta">
        The morning plan, the evening review and timed task reminders, as notifications from this browser.
        {remindersEnabled ? '' : ' Reminders are currently off for your account; turning them on here turns them on everywhere.'}
      </p>
      {!pushSupported ? (
        <p className="text-sm text-tertiary">This browser doesn't support notifications.</p>
      ) : registered ? (
        <Button busy={busy} onClick={() => void turnOff()}>Turn off on this browser</Button>
      ) : (
        <Button variant="primary" busy={busy} disabled={permission === 'denied'} onClick={() => void turnOn()}>
          <Icon name="bell" size={15} strokeWidth={2.4} /> Turn on
        </Button>
      )}
      {permission === 'denied' ? (
        <p className="mt-2 text-xs text-tertiary">Notifications are blocked for this site — allow them in the browser's site settings first.</p>
      ) : null}
      {note ? <p className="mt-2 text-xs text-replan">{note}</p> : null}
    </Card>
  );
}

/* ---------- connected apps ---------- */

/** The AI agents with access to the account (docs/mcp.md §7) — the standard
    "third-party apps with account access" page every OAuth provider offers,
    here as a card. Read on open, never cached: it is server-owned state, and
    the one thing a user does here is make a row disappear. */
function ConnectedAppsCard() {
  const api = useApi();
  const [connections, setConnections] = useState<ServerConnection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ServerConnection | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setConnections(await listConnections(api));
      setError(null);
    } catch {
      setError("Couldn't load connected apps.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const disconnect = async (c: ServerConnection) => {
    setBusy(true);
    try {
      await disconnectConnection(api, c.clientId);
      setConnections((cur) => cur?.filter((x) => x.clientId !== c.clientId) ?? null);
      setConfirm(null);
    } catch {
      setError(`Couldn't disconnect ${connectionLabel(c)}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rise-in">
      <CardTitle>Connected apps</CardTitle>
      <p className="mb-4 max-w-[60ch] text-[14px] leading-[1.5] text-meta">
        AI assistants you have connected to Habishine can read and change your tasks and habits on your behalf.
        Connect one by adding <code className="rounded bg-card-sunk px-1.5 py-0.5 text-[12.5px] text-ink">https://mcp.habishine.com/mcp</code> in
        Claude, ChatGPT or any other assistant that supports connectors, then sign in.{' '}
        <a className="font-semibold text-frog hover:underline" href="https://habishine.com/ai.html" target="_blank" rel="noreferrer">Step-by-step instructions</a>
      </p>
      {connections === null && !error ? (
        <p className="text-sm text-tertiary">Loading…</p>
      ) : connections && connections.length === 0 ? (
        <p className="text-sm text-tertiary">Nothing is connected.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {connections?.map((c) => (
            <li key={c.clientId} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold text-ink">{connectionLabel(c)}</div>
                <div className="text-[12.5px] text-meta">
                  Connected {formatWhen(c.connectedAt)} · last used {formatWhen(c.lastUsedAt)}
                  {c.sessions > 1 ? ` · ${c.sessions} sign-ins` : ''}
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={() => setConfirm(c)}>Disconnect</Button>
            </li>
          ))}
        </ul>
      )}
      {error ? <p className="mt-2 text-xs text-replan">{error}</p> : null}
      {confirm ? (
        <Dialog
          title={`Disconnect ${connectionLabel(confirm)}?`}
          onClose={() => setConfirm(null)}
          width="sm"
          footer={
            <>
              <Button onClick={() => setConfirm(null)}>Keep</Button>
              <Button variant="danger" busy={busy} onClick={() => void disconnect(confirm)}>Disconnect</Button>
            </>
          }
        >
          <p className="text-[14.5px] leading-[1.5] text-ink-2">
            It loses access right away. Nothing you did together is removed, and you can connect it again any time by signing in from the assistant.
          </p>
        </Dialog>
      ) : null}
    </Card>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'recently';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
